/**
 * The badges a node wears, wherever it appears.
 *
 * They live together, and outside the two surfaces that use them, because §4.6
 * asks for the external marker "everywhere they appear" — a phrase that is only
 * true if there is one marker rather than one per screen. The same argument
 * covers review state: a playbook past its date must read the same way in the
 * tree and in a search result, or the tree becomes the place stale documents
 * hide.
 *
 * Every map here is an exhaustive `Record` over a wire enum rather than a
 * lookup with a fallback. A value added to `DocumentStatus` or `DocumentType`
 * should be a compile error in this file, not a blank badge in production —
 * which is the same reason `api/types.ts` narrows those fields at all.
 */

import type { ReactNode } from 'react';
import type { DocumentStatus, DocumentType, NodeType } from '../../api/types.ts';
import { formatDay } from '../format.ts';
import { Badge, type BadgeTone } from '../ui/badge.tsx';

/**
 * A badge with an explanation attached.
 *
 * The `title` sits on a wrapper because `Badge` takes no DOM props, and adding
 * one to the kit for this would be a change to a component four other screens
 * render. Every marker here means something a person has to be told once —
 * "external" and "needs review" are both statements about who can see what —
 * and a badge nobody can hover to understand is decoration.
 */
function Marker({
  tone,
  title,
  children,
}: {
  tone: BadgeTone;
  title: string;
  children: ReactNode;
}) {
  return (
    <span title={title} className="inline-flex">
      <Badge tone={tone}>{children}</Badge>
    </span>
  );
}

// --- node types (§4.5) -----------------------------------------------------

const NODE_TYPE_TONES: Record<NodeType, BadgeTone> = {
  document: 'brand',
  file: 'outline',
  link: 'gray',
};

/** The same tones `routes/spaces/detail.tsx` already paints the tree with. */
export function NodeTypeBadge({ type }: { type: NodeType }) {
  return <Badge tone={NODE_TYPE_TONES[type]}>{type}</Badge>;
}

/**
 * §4.6 mitigation 1 — "links display an explicit external marker everywhere
 * they appear".
 *
 * The wording is deliberate: not "external link" as a category, but a statement
 * that this system does not decide who may open it. A client clicking a Figma
 * link they were never invited to hits Figma's wall, and the risk running the
 * other way — a Drive doc shared wider than intended — is exactly what nobody
 * checks unless the boundary is visible where the link is.
 *
 * `url` is optional because the tree cannot get one: `GET /api/spaces/:id/nodes`
 * returns nodes, not payloads. The marker still appears there, because the
 * marker is the half §4.6 requires; the destination is the half it would be
 * nice to have.
 */
export function ExternalLinkMarker({ url }: { url?: string | null }) {
  return (
    <Marker
      tone="warning"
      title={
        url
          ? `Opens ${url} outside Matchbox. Whoever owns that site decides who may read it.`
          : 'Opens outside Matchbox. Whoever owns that site decides who may read it.'
      }
    >
      external <span aria-hidden="true">↗</span>
    </Marker>
  );
}

// --- document types and statuses (§7.1, §7.4) ------------------------------

/** §7.1's first column, in the words a person reads rather than the enum's. */
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  playbook: 'Playbook',
  reference: 'Reference',
  project_note: 'Project note',
  status_update: 'Status update',
  deliverable: 'Deliverable',
};

export function DocumentTypeBadge({ type }: { type: DocumentType }) {
  return <Badge tone="outline">{DOCUMENT_TYPE_LABELS[type]}</Badge>;
}

const STATUS_TONES: Record<DocumentStatus, BadgeTone> = {
  draft: 'gray',
  // Reachable as of 1b: a document enters it when an approval request is opened
  // on it (§7.6). Grouped with `draft` in the search status rules for the same
  // reason it is grey here — both are the pre-publication pair, and only people
  // who can edit the document see either.
  in_review: 'gray',
  published: 'success',
  // §7.2 — "stale beats hidden". A warning, never a hide.
  needs_review: 'warning',
  archived: 'outline',
};

const STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: 'draft',
  in_review: 'in review',
  published: 'published',
  needs_review: 'needs review',
  archived: 'archived',
};

const STATUS_MEANINGS: Record<DocumentStatus, string> = {
  draft: 'Only the owner and people who can edit this node can see it (§9.3).',
  in_review: 'With its reviewers (§7.6), and visible to the same people a draft is (§9.3).',
  published: 'Anyone who can view this node reads this version.',
  needs_review: 'Past its review date and still visible — unpublishing stale documents is worse.',
  archived: 'Out of circulation. Search skips it unless archived results are asked for.',
};

export function DocumentStatusBadge({ status }: { status: DocumentStatus }) {
  return (
    <Marker tone={STATUS_TONES[status]} title={STATUS_MEANINGS[status]}>
      {STATUS_LABELS[status]}
    </Marker>
  );
}

/**
 * §7.4 — "editors with access see the draft".
 *
 * Worth a badge of its own because it is the one place two people with
 * different capabilities on the same node are reading different text, and the
 * one who can only read has no way to tell.
 */
export function WorkingDraftBadge() {
  return (
    <Marker
      tone="brand"
      title="An unpublished draft exists. Readers still see the published version."
    >
      draft in progress
    </Marker>
  );
}

// --- review dates (§7.2) ---------------------------------------------------

/**
 * Today, as the API's `date` column spells it.
 *
 * `apps/api/src/documents/serialize.ts` compares `review_by` against
 * `new Date().toISOString().slice(0, 10)`, so this does too. Using the
 * browser's *local* day instead would put the client a day ahead of the server
 * for part of every evening west of UTC — a small wrong, but one the two sides
 * would disagree about, which is the kind of difference nobody can debug from a
 * screenshot.
 */
function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Lexical comparison is correct and total for `YYYY-MM-DD`, which is why the column is one. */
export function isReviewLapsed(reviewBy: string | null | undefined, today = utcToday()): boolean {
  return reviewBy !== null && reviewBy !== undefined && reviewBy < today;
}

/**
 * "Review overdue" — §7.2's nag, where a person is already looking.
 *
 * Renders nothing when the date has not passed. There is deliberately no
 * "review current" badge: `review_by` applies to two of the five types (§7.1),
 * so a green tick on the other three would assert something nobody said, and on
 * a playbook it would decorate the ordinary case at the expense of the one that
 * matters.
 */
export function ReviewBadge({
  reviewBy,
  status,
}: {
  reviewBy: string | null;
  /** Where it is known. `needs_review` means the 07:00 sweep has already caught up. */
  status?: DocumentStatus;
}) {
  if (!isReviewLapsed(reviewBy) || !reviewBy) return null;
  return (
    <Marker
      tone="warning"
      title={
        status === 'needs_review'
          ? `Review was due ${formatDay(reviewBy)}. The daily sweep has already flagged it.`
          : `Review was due ${formatDay(reviewBy)}. It flags as needs-review at the next 07:00 sweep.`
      }
    >
      review overdue
    </Marker>
  );
}
